import React, { Component } from "react";
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
    Table,
    Button,
    Space,
    Modal,
    Form,
    Input,
    Select,
    message,
    Popconfirm,
    Tag,
    Typography,
    Card,
} from "antd";
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import "./CallbackManagement.scss";

// module
import {
    callbackList,
    callbackCreate,
    callbackUpdate,
    callbackDelete,
    callbackGetEventTypes
} from '../../../../modules/callback.js';

const { Option } = Select;
const { Title, Text } = Typography;

class CallbackManagement extends Component {
    constructor(props) {
        super(props);
        this.state = {
            callbacks: [],
            eventTypes: [],
            loading: false,
            modalVisible: false,
            editingCallback: null,
            pagination: {
                current: 1,
                pageSize: 10,
                total: 0,
            },
        };
    }

    componentDidMount() {
        this.loadEventTypes();
        this.loadCallbacks();
    }

    loadEventTypes = async () => {
        try {
            const response = await this.props.callbackGetEventTypes();
            if (response && response.success) {
                this.setState({
                    eventTypes: response.data || []
                });
            }
        } catch (error) {
            console.error('Load event types error:', error);
            // Fallback to empty array if API fails
            this.setState({ eventTypes: [] });
        }
    };

    loadCallbacks = async (page = 1, pageSize = 10) => {
        this.setState({ loading: true });
        try {
            const params = {
                page,
                limit: pageSize
            };
            const response = await this.props.callbackList(params);

            if (response && response.success) {
                this.setState({
                    callbacks: response.data.results || [],
                    pagination: {
                        ...this.state.pagination,
                        current: page,
                        total: response.data.pagination.total || 0,
                    },
                });
            }
        } catch (error) {
            message.error('Failed to load callbacks');
            console.error('Load callbacks error:', error);
        } finally {
            this.setState({ loading: false });
        }
    };

    handleCreate = () => {
        this.setState({
            modalVisible: true,
            editingCallback: null,
        });
    };

    handleEdit = (callback) => {
        this.setState({
            modalVisible: true,
            editingCallback: callback,
        });
    };

    handleDelete = async (id) => {
        try {
            const response = await this.props.callbackDelete(id);

            if (response && response.success) {
                message.success('Callback deleted successfully');
                this.loadCallbacks();
            } else {
                message.error(response.message || 'Failed to delete callback');
            }
        } catch (error) {
            message.error('Failed to delete callback');
            console.error('Delete callback error:', error);
        }
    };

    handleModalSubmit = async (values) => {
        try {
            const { editingCallback } = this.state;
            let response;

            if (editingCallback) {
                response = await this.props.callbackUpdate(editingCallback._id, {
                    ...values
                });
            } else {
                response = await this.props.callbackCreate(values);
            }

            if (response && response.success) {
                message.success(`Callback ${editingCallback ? 'updated' : 'created'} successfully`);
                this.setState({ modalVisible: false });
                this.loadCallbacks();
            } else {
                message.error(response.message || `Failed to ${editingCallback ? 'update' : 'create'} callback`);
            }
        } catch (error) {
            message.error(`Failed to ${this.state.editingCallback ? 'update' : 'create'} callback`);
            console.error('Submit callback error:', error);
        }
    };

    handleTableChange = (pagination) => {
        this.loadCallbacks(pagination.current, pagination.pageSize);
    };

    getEventTypeLabel = (value) => {
        const eventType = this.state.eventTypes.find(type => type.value === value);
        return eventType ? eventType.displayName : value;
    };

    render() {
        const { callbacks, eventTypes, loading, modalVisible, editingCallback, pagination } = this.state;

        const columns = [
            {
                title: 'Callback URL',
                dataIndex: 'callbackUrl',
                key: 'callbackUrl',
                ellipsis: true,
            },
            {
                title: 'Event Type',
                dataIndex: 'eventType',
                key: 'eventType',
                render: (eventType) => this.getEventTypeLabel(eventType),
            },
            {
                title: 'Created At',
                dataIndex: 'createdAt',
                key: 'createdAt',
                render: (date) => new Date(date).toLocaleString(),
            },
            {
                title: 'Actions',
                key: 'actions',
                width: 120,
                render: (_, record) => (
                    <Space>
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => this.handleEdit(record)}
                            size="small"
                        />
                        <Popconfirm
                            title="Are you sure you want to delete this callback?"
                            onConfirm={() => this.handleDelete(record._id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                            />
                        </Popconfirm>
                    </Space>
                ),
            },
        ];

        return (
            <div className="callback-management">
                <Card className="callback-table-card">
                    <div className="table-header">
                        <div className="header-left">
                            <h3>Callbacks ({pagination.total})</h3>
                        </div>
                        <div>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => { this.loadCallbacks() }}
                                loading={loading}
                                style={{ marginRight: '10px' }}
                            >
                                Refresh
                            </Button>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={this.handleCreate}
                            >
                                Add Callback
                            </Button>
                        </div>
                    </div>

                    <Table
                        columns={columns}
                        dataSource={callbacks}
                        loading={loading}
                        rowKey="_id"
                        pagination={{
                            ...pagination,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} of ${total} items`,
                        }}
                        onChange={this.handleTableChange}
                        size="middle"
                    />
                </Card>

                <CallbackModal
                    visible={modalVisible}
                    callback={editingCallback}
                    eventTypes={eventTypes}
                    onSubmit={this.handleModalSubmit}
                    onCancel={() => this.setState({ modalVisible: false })}
                />
            </div>
        );
    }
}

// Callback Modal Component
class CallbackModal extends Component {
    formRef = React.createRef();

    componentDidUpdate(prevProps) {
        if (this.props.visible && !prevProps.visible) {
            if (this.props.callback) {
                this.formRef.current?.setFieldsValue(this.props.callback);
            } else {
                this.formRef.current?.resetFields();
            }
        }
    }

    handleSubmit = () => {
        this.formRef.current?.validateFields().then((values) => {
            this.props.onSubmit(values);
        });
    };

    render() {
        const { visible, callback, eventTypes, onCancel } = this.props;

        return (
            <Modal
                title={callback ? 'Edit Callback' : 'Add Callback'}
                open={visible}
                onOk={this.handleSubmit}
                onCancel={onCancel}
                okText={callback ? 'Update' : 'Create'}
                width={600}
            >
                <Form
                    ref={this.formRef}
                    layout="vertical"
                    initialValues={{
                        callbackUrl: '',
                        eventType: '',
                    }}
                >
                    <Form.Item
                        label="Callback URL"
                        name="callbackUrl"
                        rules={[
                            { required: true, message: 'Please input callback URL!' },
                            { type: 'url', message: 'Please enter a valid URL!' },
                        ]}
                    >
                        <Input placeholder="https://api.example.com/webhook" />
                    </Form.Item>

                    <Form.Item
                        label="Event Type"
                        name="eventType"
                        rules={[
                            { required: true, message: 'Please select event type!' },
                        ]}
                    >
                        <Select placeholder="Select event type">
                            {eventTypes.map(type => (
                                <Option key={type.value} value={type.value}>
                                    {type.displayName}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        );
    }
}

const mapStateToProps = (state) => ({
    // Map Redux state to props if needed
});

const mapDispatchToProps = (dispatch) => {
    return bindActionCreators({
        callbackList,
        callbackCreate,
        callbackUpdate,
        callbackDelete,
        callbackGetEventTypes,
    }, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(CallbackManagement);
